import type React from "react";

import type { InterswitchTransactionData } from "../../../interface/interfaces";

const TransactionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  transaction: InterswitchTransactionData | null;
}> = ({ isOpen, onClose, transaction }) => {
  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200">
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Transaction Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-red-500 text-3xl font-light transition-colors duration-200 hover:bg-red-50 rounded-full w-10 h-10 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          <div className="space-y-6">
            {/* Interswitch Payment ID - Full Width at Top */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200">
              <label className="block text-sm font-semibold text-indigo-700 mb-2">
                Interswitch Payment ID
              </label>
              <p className="text-lg font-mono text-indigo-900 break-all bg-white p-3 rounded-lg border border-indigo-200">
                {transaction.interswitchPaymentId || "null"}
              </p>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-200">
                <label className="block text-sm font-semibold text-emerald-700 mb-2">
                  Payment ID
                </label>
                <p className="text-emerald-900 font-medium">
                  {transaction.paymentId}
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200">
                <label className="block text-sm font-semibold text-amber-700 mb-2">
                  Reference
                </label>
                <p className="text-amber-900 font-medium">
                  {transaction.reference}
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-4 rounded-xl border border-rose-200">
                <label className="block text-sm font-semibold text-rose-700 mb-2">
                  Response Code
                </label>
                <p className="text-rose-900 font-medium">
                  {transaction.interswitchResponseCode}
                </p>
              </div>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-xl border border-violet-200">
                <label className="block text-sm font-semibold text-violet-700 mb-2">
                  HTTP Response Code
                </label>
                <p className="text-violet-900 font-medium">
                  {transaction.httpResponseCode}
                </p>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-sky-50 p-4 rounded-xl border border-cyan-200">
                <label className="block text-sm font-semibold text-cyan-700 mb-2">
                  Visa ECI Flag
                </label>
                <p className="text-cyan-900 font-medium">
                  {transaction.visaEciFlag || "N/A"}
                </p>
              </div>

              <div className="bg-gradient-to-br from-lime-50 to-green-50 p-4 rounded-xl border border-lime-200">
                <label className="block text-sm font-semibold text-lime-700 mb-2">
                  Visa Transaction ID
                </label>
                <p className="text-lime-900 font-medium">
                  {transaction.visaTransactionId || "N/A"}
                </p>
              </div>
            </div>

            {/* Response Message - Full Width */}
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-6 rounded-xl border border-slate-200">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Response Message
              </label>
              <p className="text-slate-900 bg-white p-3 rounded-lg border border-slate-200">
                {transaction.interswitchResponseMessage}
              </p>
            </div>

            {/* Visa Term URL - Full Width */}
            <div className="bg-gradient-to-r from-fuchsia-50 to-pink-50 p-6 rounded-xl border border-fuchsia-200">
              <label className="block text-sm font-semibold text-fuchsia-700 mb-2">
                Visa Term URL
              </label>
              <p className="text-fuchsia-900 break-all bg-white p-3 rounded-lg border border-fuchsia-200 font-mono text-sm">
                {transaction.visaTermUrl || "N/A"}
              </p>
            </div>

            {/* Dates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                <label className="block text-sm font-semibold text-blue-700 mb-2">
                  Date Created
                </label>
                <p className="text-blue-900 font-medium text-sm">
                  {new Date(transaction.dateCreated).toLocaleString()}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-200">
                <label className="block text-sm font-semibold text-purple-700 mb-2">
                  Date Updated
                </label>
                <p className="text-purple-900 font-medium text-sm">
                  {new Date(transaction.dateUpdated).toLocaleString()}
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-4 rounded-xl border border-teal-200">
                <label className="block text-sm font-semibold text-teal-700 mb-2">
                  Date Completed
                </label>
                <p className="text-teal-900 font-medium text-sm">
                  {transaction.dateCompleted
                    ? new Date(transaction.dateCompleted).toLocaleString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-8 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;
